import { msg } from "../../locales/translate.ts";
import type { CursorPage } from "../../modules/cursorExport/cursorExport";
import { formatReserveAsOf, parseRollingReserves } from "./rollingReserveData.ts";

export interface RollingReserveTransaction {
	[key: string]: unknown;
	id: number;
	mid: number;
	mid_name?: string | null;
	created_at: string;
	settlement_date?: string | null;
	amount: number;
	currency: string;
	rolling_reserve_amount: number;
	rolling_reserver_percent?: number | null;
	reserve_release_date?: string | null;
	channel_alias?: string | null;
}

export const ROLLING_TRANSACTIONS_PAGE_SIZE = 50;
export const ROLLING_TRANSACTIONS_EXPORT_PAGE_SIZE = 500;

export const getRollingTransactionsRequest = (clientId: number, currency: string, projectId: number, cursor?: string, perPage = ROLLING_TRANSACTIONS_PAGE_SIZE) => ({
	method: "rollings/trx/list",
	apiVersion: 2 as const,
	expectedProjectId: projectId,
	headers: { ignoreBusy: "true" },
	data: { client_id: clientId, currency, per_page: perPage, ...(cursor !== undefined ? { cursor } : {}) },
});

const isRecord = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
const isCount = (value: unknown): value is number => typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
const isAmount = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

const parseRows = (rows: unknown, currency: string): RollingReserveTransaction[] => {
	if (!Array.isArray(rows) || rows.some(row => !isRecord(row) || !isCount(row.id) || !isCount(row.mid) || row.currency !== currency ||
		!isAmount(row.amount) || !isAmount(row.rolling_reserve_amount) || typeof row.created_at !== "string" ||
		["mid_name", "settlement_date", "reserve_release_date", "channel_alias"].some(key => row[key] !== undefined && row[key] !== null && typeof row[key] !== "string") ||
		(row.rolling_reserver_percent !== undefined && row.rolling_reserver_percent !== null && !isAmount(row.rolling_reserver_percent)))) {
		throw new Error(msg("unexpectedRollingReserveTransactions"));
	}
	return rows as RollingReserveTransaction[];
};

// HTTP already unwraps the API envelope. Pagination lives inside data.pages.
export const parseRollingTransactions = (data: unknown, clientId: number, currency: string) => {
	if (!isRecord(data) || data.client_id !== clientId) throw new Error(msg("unexpectedRollingReserveContext"));
	const rows = parseRows(data.rows, currency);
	if (!isRecord(data.pages)) throw new Error(msg("missingRollingReservePagination"));
	const { next_cursor: nextCursor, total, perPage } = data.pages;
	if (nextCursor !== null && (typeof nextCursor !== "string" || !nextCursor.trim())) throw new Error(msg("unexpectedRollingReserveCursor"));
	if (!isCount(total) || !isCount(perPage) || perPage < 1 || rows.length > perPage || rows.length > total) throw new Error(msg("unexpectedRollingReservePagination"));
	const summary = data.totals === undefined ? undefined : parseRollingReserves({ client_id: clientId, currencies: data.totals }, clientId).find(row => row.currency === currency);
	return { rows, nextCursor, total, perPage, asOf: formatReserveAsOf(data.as_of), summary };
};

export const parseRollingTransactionCursorPage = (data: unknown, clientId: number, currency: string): CursorPage<RollingReserveTransaction> => {
	const page = parseRollingTransactions(data, clientId, currency);
	return { rows: page.rows, nextCursor: page.nextCursor, total: page.total };
};

// Keep the server's calendar date, particularly for midnight UTC release dates.
export const formatRollingTransactionDate = (value: string | null | undefined, dateOnly = false) => {
	if (!value) return "—";
	const formatted = formatReserveAsOf(value.replace("T", " ").slice(0, 19));
	return formatted ? dateOnly ? formatted.split(" ")[0] : formatted : "—";
};

const csvCell = (value: unknown) => {
	let text = value === null || value === undefined ? "" : typeof value === "object" ? JSON.stringify(value) : String(value);
	if (typeof value !== "number" && !/^-?\d+(?:\.\d+)?$/.test(text) && (/^\s*[=+@-]/.test(text) || /^[\t\r\n]/.test(text))) text = `'${text}`;
	return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const buildRollingTransactionsCsv = (data: unknown, currency: string) => {
	// Only a fully collected row array may be converted, never a single server page.
	if (!Array.isArray(data)) throw new Error(msg("collectAllRecordsBeforeCreatingCsv"));
	const rows = parseRows(data, currency);
	if (!rows.length) throw new Error(msg("noHeldTransactionsToExport"));
	const priority = ["id", "created_at", "mid", "mid_name", "amount", "currency", "rolling_reserve_amount", "rolling_reserver_percent", "reserve_release_date"];
	const available = Array.from(new Set(rows.flatMap(row => Object.keys(row))))
		.filter(key => !["processing_mid", "processing_id", "current_channel_index"].includes(key));
	const headers = [...priority.filter(key => available.includes(key)), ...available.filter(key => !priority.includes(key)).sort()];
	// This endpoint returns amount and reserve amounts in major units. Preserve them as sent.
	const lines = rows.map(row => headers.map(header => csvCell(row[header])).join(","));
	return "\uFEFF" + [headers.map(csvCell).join(","), ...lines].join("\r\n") + "\r\n";
};

/** Fetch exactly one page using an already discovered cursor. Never walk intermediate pages. */
export const loadRollingTransactionPage = async (
	page: number,
	cursors: (string | undefined)[],
	load: (cursor: string | undefined) => Promise<ReturnType<typeof parseRollingTransactions>>,
	signal: AbortSignal,
) => {
	if (!Number.isSafeInteger(page) || page < 0 || page >= cursors.length) throw new Error("Unknown page cursor");
	signal.throwIfAborted();
	const data = await load(cursors[page]);
	signal.throwIfAborted();
	const next = data.nextCursor;
	if (next !== null && cursors.slice(0, page + 1).includes(next)) throw new Error("Repeated cursor");
	if (cursors[page + 1] !== next) cursors.splice(page + 1);
	if (next !== null) cursors[page + 1] = next;
	return data;
};
